import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { getAllCities } from "@/data/cities";
import { isKnownLocality } from "@/utils/cityArea";
import MasiniSecondHand from "@/pages/MasiniSecondHand";
import { routeHead } from "@/lib/seo/route-head";
import { listingsV2QueryOptions } from "@/lib/queries/publicData";
import { citySlug } from "@/utils/citySlug";

export const Route = createFileRoute("/masini-second-hand/$slug/")({
  head: ({ params }) => routeHead("/masini-second-hand/$slug/", params as Record<string, string>),
  // Prefetch pentru randare server-side a listei de anunțuri (SEO).
  // Brand slugs show exactly the same listings as /cars/{brand}, which is the
  // single canonical brand URL, so they 301 there. City slugs stay here.
  loader: async ({ context, params }) => {
    const listings = await context.queryClient.ensureQueryData(
      listingsV2QueryOptions({ country_code: "RO", limit: 2000 }),
    );
    const slug = citySlug(params.slug);
    const isBrand = (listings as Array<{ make?: string | null }> | undefined)?.some(
      (l) => l.make && citySlug(l.make) === slug,
    );
    if (isBrand) throw redirect({ href: `/cars/${slug}`, statusCode: 301 });
    // City slugs have the same intent and geography as /masini-de-vanzare-{city}
    // (which lists the city first, county alternatives separately): 301 there.
    // Anything that is neither a brand nor a real locality is a 404.
    const known = isKnownLocality(slug, getAllCities("RO").map((c) => c.slug), listings as never);
    if (known) throw redirect({ href: `/masini-de-vanzare-${slug}`, statusCode: 301 });
    throw notFound();
  },
  errorComponent: function ListingsError({ error }: { error: unknown }) {
    return (
      <main className="container mx-auto max-w-7xl px-4 py-12">
        <h1 className="text-3xl font-bold">Anunțurile nu pot fi încărcate momentan</h1>
        <p className="mt-3 text-muted-foreground" role="alert">{error instanceof Error ? error.message : String(error)}</p>
      </main>
    );
  },
  notFoundComponent: () => (
    <main className="container mx-auto max-w-7xl px-4 py-12">
      <h1 className="text-3xl font-bold">Pagina nu a fost găsită</h1>
      <p className="mt-3 text-muted-foreground">Nu avem o pagină pentru această adresă.</p>
      <a href="/cars" className="mt-6 inline-block rounded-lg bg-primary px-5 py-3 font-medium text-primary-foreground">Caută mașini</a>
    </main>
  ),
  component: RouteComponent,
});

function RouteComponent() {
  const listings = Route.useLoaderData();
  if (!listings) throw new Error("Datele SSR pentru anunțuri lipsesc");
  return (
    <MasiniSecondHand initialListings={listings} />
  );
}
